import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  Group,
  Indicator,
  List,
  Space,
  Stack,
  Sx,
  Text as MantineText,
  ThemeIcon,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { FC, useContext, useState } from 'react';
import { IconArrowRight, IconCheck, IconChevronDown, IconPackage, IconUsers } from '@tabler/icons';
// eslint-disable-next-line import/no-unresolved
import { A11y, Navigation, Pagination } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import PanePaper from '../../../Molecules/PanePaper';
import { DataS } from '../../../Service/DataS';
import { AddonDTO, BillingAddon, BillingInterval } from '../../../Types/Billing';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { BookitupSubscriptionContext } from '../../../Service/Context/BookitupSubscriptionContext';
import { IncDecCounterv2 } from '../../../Atoms/inputs/IncDecCounter';
import { ChargebeeS } from '../../../Service/ChargebeeS';
import { AuthContext } from '../../../Service/Context/AuthContext';
import { WindowContext } from '../../../Service/Context/WindowContext';

const RESOURCE_ADDON_NAMES = ['Resource-Planning', 'Ressourcen'];
const ACTIVE_BILLING_COLOR = '#FE8047';

const mobileCardSwiperSx: Sx = (theme) => ({
  marginLeft: -4,
  marginRight: -4,
  '.swiper': {
    paddingLeft: 4,
    paddingRight: 4,
    paddingBottom: 30,
    overflow: 'visible',
  },
  '.swiper-wrapper': {
    alignItems: 'stretch',
  },
  '.swiper-slide': {
    height: 'auto',
    display: 'flex',
  },
  '.swiper-slide > .mantine-Indicator-root': {
    width: '100%',
    maxWidth: '100%',
  },
  '.swiper-pagination': {
    bottom: 0,
  },
  '.swiper-button-prev, .swiper-button-next': {
    width: 26,
    height: 26,
    marginTop: -24,
    borderRadius: 26,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.white,
    boxShadow: theme.colorScheme === 'dark' ? '0 3px 10px rgba(0, 0, 0, 0.24)' : '0 3px 10px rgba(29, 81, 145, 0.12)',
    color: ACTIVE_BILLING_COLOR,
    '&::after': {
      fontSize: 10,
      fontWeight: 900,
    },
  },
  '.swiper-button-prev': {
    left: 0,
  },
  '.swiper-button-next': {
    right: 0,
  },
  '.swiper-button-disabled': {
    opacity: 0.28,
  },
  '.swiper-pagination-bullet': {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[4],
    opacity: 1,
  },
  '.swiper-pagination-bullet-active': {
    backgroundColor: ACTIVE_BILLING_COLOR,
  },
});

const normalizeAddonName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const isResourceAddonName = (name: string) =>
  RESOURCE_ADDON_NAMES.some((addonName) => normalizeAddonName(name).startsWith(normalizeAddonName(addonName)));

const findActiveAddon = (addons: AddonDTO[] | undefined, addonName: string) => {
  if (!addons || addons.length === 0) return undefined;
  const normalizedAddonName = normalizeAddonName(addonName);
  return addons.find((addon) => {
    const normalizedActiveAddonName = normalizeAddonName(addon.name);
    return (
      normalizedActiveAddonName.startsWith(normalizedAddonName) ||
      (isResourceAddonName(addonName) && isResourceAddonName(addon.name))
    );
  });
};

const AddonPriceRow: FC<{ label: string; amount: number }> = ({ label, amount }) => (
  <Group position="apart" spacing="xs" py={5}>
    <MantineText size="xs" color="dimmed">
      {label}
    </MantineText>
    <MantineText size="xs" weight={800}>
      {amount} € / Monat
    </MantineText>
  </Group>
);

const AddonPrice = ({ addon, billingInterval }: { addon: BillingAddon; billingInterval: BillingInterval }) => {
  const [pricingExpanded, setPricingExpanded] = useState(false);
  const { colors, colorScheme } = useMantineTheme();
  const { price } = addon;

  if (price.strategy === 'perUnit') {
    const amount = price.rates[billingInterval];
    return (
      <Box
        p="md"
        sx={{
          borderRadius: 10,
          textAlign: 'center',
          backgroundColor: colorScheme === 'dark' ? colors.dark[7] : colors.gray[0],
        }}
      >
        <Group position="center" align="baseline" spacing={4} noWrap>
          <MantineText weight={900} size={30} sx={{ lineHeight: 1 }}>
            {amount} €
          </MantineText>
          <MantineText size="xs" color="dimmed">
            / Monat
          </MantineText>
        </Group>
        <MantineText mt={6} align="center" size={10} color="dimmed">
          pro {price.unitLabel} · zzgl. MwSt.
        </MantineText>
      </Box>
    );
  }

  const startingPrice = price.tiers[billingInterval][0]?.price;

  return (
    <Stack spacing="sm">
      {startingPrice && (
        <Box
          p="md"
          sx={{
            borderRadius: 10,
            textAlign: 'center',
            backgroundColor: colorScheme === 'dark' ? colors.dark[7] : colors.gray[0],
          }}
        >
          <Group position="center" align="baseline" spacing={4} noWrap>
            <MantineText size="xs" color="dimmed">
              ab
            </MantineText>
            <MantineText weight={900} size={30} sx={{ lineHeight: 1 }}>
              {startingPrice} €
            </MantineText>
            <MantineText size="xs" color="dimmed">
              / Monat
            </MantineText>
          </Group>
          <MantineText mt={6} align="center" size={10} color="dimmed">
            passend zu deinem Bestand · zzgl. MwSt.
          </MantineText>
        </Box>
      )}

      <UnstyledButton
        type="button"
        aria-expanded={pricingExpanded}
        onClick={() => setPricingExpanded((current) => !current)}
        sx={(theme) => ({
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          color: theme.colorScheme === 'dark' ? theme.colors.gray[3] : theme.colors.gray[7],
          fontSize: 12,
          fontWeight: 800,
        })}
      >
        <IconChevronDown
          size={15}
          style={{ transform: pricingExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 160ms ease' }}
        />
        {pricingExpanded ? 'Preise ausblenden' : 'Preisstaffel ansehen'}
      </UnstyledButton>
      <Collapse in={pricingExpanded}>
        <Box
          p="sm"
          sx={{
            borderRadius: 8,
            backgroundColor: colorScheme === 'dark' ? colors.dark[7] : colors.gray[0],
          }}
        >
          <MantineText size={10} weight={800} color="dimmed" transform="uppercase" mb={3}>
            Anzahl der {price.unitLabel}
          </MantineText>
          {price.tiers[billingInterval].map((tier) => {
            const { from, to, price: tierAmount } = tier;
            return <AddonPriceRow key={tierAmount} label={`${from} – ${to}`} amount={tierAmount} />;
          })}
        </Box>
      </Collapse>
    </Stack>
  );
};

const AddonFeatures: FC<{ features: string[] }> = ({ features }) => (
  <Box>
    <MantineText size="sm" weight={900} mb="sm">
      Das bekommst du dazu
    </MantineText>
    <List spacing={7} size="xs" icon={<IconCheck size={13} stroke={3} color="#FE8047" />}>
      {features.map((feature) => (
        <List.Item key={feature}>{feature}</List.Item>
      ))}
    </List>
  </Box>
);

const getAddonQuantitySteps = (addon: BillingAddon, billingInterval: BillingInterval) => {
  if (addon.price.strategy !== 'tiered') return undefined;
  return addon.price.tiers[billingInterval].map((tier) => tier.from);
};

const formatAddonQuantity = (addon: BillingAddon, billingInterval: BillingInterval, quantity: number) => {
  if (addon.price.strategy !== 'tiered') return quantity;
  const tier = addon.price.tiers[billingInterval].find(({ from, to }) => quantity >= from && quantity <= to);
  return tier ? `${tier.from} - ${tier.to}` : quantity;
};

const getAddonCheckoutQuantity = (addon: BillingAddon, billingInterval: BillingInterval, quantity: number) => {
  if (addon.price.strategy !== 'tiered') return quantity;
  const tier = addon.price.tiers[billingInterval].find(({ from, to }) => quantity >= from && quantity <= to);
  return tier?.to ?? quantity;
};

const AvailableAddonCard: FC<{
  addon: BillingAddon;
  activeAddons?: AddonDTO[];
  billingInterval: BillingInterval;
  onAddonCheckoutSuccess: () => void;
  openTrialModal: () => unknown;
  hasActiveLicence: boolean;
  trialAvailable: boolean;
}> = ({
  addon,
  activeAddons,
  billingInterval,
  onAddonCheckoutSuccess,
  openTrialModal,
  hasActiveLicence,
  trialAvailable,
}) => {
  const [quantity, setQuantity] = useState(1);
  const { colors, colorScheme } = useMantineTheme();
  const { name, internalName, shortDescription, features } = addon;
  const activeAddon = findActiveAddon(activeAddons, name);
  const isResourceAddon = isResourceAddonName(name);
  const isTeamAddon = normalizeAddonName(name) === 'team';
  const isAddonActive = Boolean(activeAddon);
  const canChangeBillingInterval = isAddonActive && activeAddon?.interval !== billingInterval;
  const canAddAddon = !activeAddon;
  const quantitySteps = getAddonQuantitySteps(addon, billingInterval);

  const openAddonCheckout = () => {
    const addonId = `${internalName}-EUR-${billingInterval}`;
    const checkoutQuantity = getAddonCheckoutQuantity(addon, billingInterval, activeAddon?.quantity ?? quantity);
    ChargebeeS.openHostedAddonCheckout(addonId, checkoutQuantity, onAddonCheckoutSuccess);
  };

  const card = (
    <PanePaper
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        padding: 0,
        border: `${activeAddon ? '2px' : '1px'} solid ${
          // eslint-disable-next-line no-nested-ternary
          activeAddon ? ACTIVE_BILLING_COLOR : colorScheme === 'dark' ? colors.dark[4] : colors.gray[1]
        }`,
        boxShadow: colorScheme === 'dark' ? '0 12px 32px rgba(0, 0, 0, 0.16)' : '0 12px 34px rgba(29, 81, 145, 0.08)',
      }}
    >
      <Box
        sx={{
          height: 5,
          background: isTeamAddon
            ? 'linear-gradient(90deg, #4C8BF5 0%, #8B5CF6 100%)'
            : 'linear-gradient(90deg, #FE8047 0%, #F6B73C 100%)',
        }}
      />
      <Stack spacing="lg" p="lg">
        <Box>
          <Group position="apart" align="flex-start" spacing="sm" noWrap>
            <Group spacing="md" noWrap>
              <ThemeIcon
                radius="md"
                size={42}
                variant="light"
                color={isTeamAddon ? 'blue' : 'orange'}
                sx={(theme) => ({
                  color: isTeamAddon ? theme.colors.blue[8] : theme.colors.orange[9],
                  backgroundColor:
                    theme.colorScheme === 'dark'
                      ? theme.fn.rgba(isTeamAddon ? theme.colors.blue[4] : theme.colors.orange[4], 0.18)
                      : theme.fn.rgba(isTeamAddon ? theme.colors.blue[1] : theme.colors.orange[1], 0.82),
                })}
              >
                {isTeamAddon ? <IconUsers size={22} stroke={2.4} /> : <IconPackage size={22} stroke={2.4} />}
              </ThemeIcon>
              <Box sx={{ minWidth: 0 }}>
                <MantineText size={10} color="dimmed" transform="uppercase" weight={800} sx={{ letterSpacing: 0.8 }}>
                  Flexibel erweiterbar
                </MantineText>
                <MantineText mt={2} weight={900} size={22}>
                  {name}
                </MantineText>
              </Box>
            </Group>
            {!activeAddon && (
              <Badge
                size="sm"
                radius="sm"
                variant="light"
                color={isTeamAddon ? 'blue' : 'orange'}
                sx={(theme) => ({
                  flex: '0 0 auto',
                  textTransform: 'none',
                  color: isTeamAddon ? theme.colors.blue[9] : theme.colors.orange[9],
                  backgroundColor:
                    theme.colorScheme === 'dark'
                      ? theme.fn.rgba(isTeamAddon ? theme.colors.blue[5] : theme.colors.orange[5], 0.22)
                      : theme.fn.rgba(isTeamAddon ? theme.colors.blue[1] : theme.colors.orange[1], 0.88),
                })}
              >
                Optional
              </Badge>
            )}
          </Group>
          <MantineText color="dimmed" mt="md" size="xs" sx={{ lineHeight: 1.55 }}>
            {shortDescription}
          </MantineText>
        </Box>

        <AddonPrice addon={addon} billingInterval={billingInterval} />

        {(canAddAddon || canChangeBillingInterval) && (
          <Box
            p="md"
            sx={{
              borderRadius: 10,
              backgroundColor: colorScheme === 'dark' ? colors.dark[7] : colors.gray[0],
            }}
          >
            {canAddAddon && (
              <IncDecCounterv2
                initialCount={quantity}
                min={1}
                deleteDisabled
                onChanged={setQuantity}
                unit={addon.price.unitLabel}
                steps={quantitySteps}
                formatValue={(count) => formatAddonQuantity(addon, billingInterval, count)}
              />
            )}
            <Center mt={canAddAddon ? 'lg' : 0}>
              <Button
                fullWidth
                variant="gradient"
                gradient={{ from: isTeamAddon ? '#4C8BF5' : '#FE8047', to: isTeamAddon ? '#8B5CF6' : '#F6B73C' }}
                size="sm"
                disabled={!hasActiveLicence}
                onClick={openAddonCheckout}
                rightIcon={<IconArrowRight size={15} />}
              >
                {canChangeBillingInterval ? 'Abrechnung anpassen' : 'Add-on hinzufügen'}
              </Button>
            </Center>

            {!hasActiveLicence && (
              <MantineText size={10} mt={6} color="orange" align="center">
                Wähle zuerst einen kostenpflichtigen Plan.
              </MantineText>
            )}
          </Box>
        )}
        {isResourceAddon && trialAvailable && !activeAddon && (
          <Center mt={-8}>
            <Button variant="subtle" color="orange" onClick={openTrialModal}>
              Erst kostenlos ausprobieren
            </Button>
          </Center>
        )}
        <AddonFeatures features={features} />
      </Stack>
    </PanePaper>
  );

  return (
    <Indicator
      disabled={!isAddonActive && !isTeamAddon}
      label={
        <MantineText color="white" size={11} weight="bold">
          {isAddonActive ? 'Aktiv' : 'Neu'}
        </MantineText>
      }
      position="top-center"
      radius="md"
      size={18}
      styles={(theme) => ({
        indicator: {
          backgroundColor: ACTIVE_BILLING_COLOR,
          color: theme.white,
          width: 50,
        },
      })}
      sx={{
        flex: '1 1 320px',
        maxWidth: 390,
        width: '100%',
        '@media (max-width: 576px)': {
          maxWidth: '100%',
        },
      }}
    >
      {card}
    </Indicator>
  );
};

const AvailableAddons: FC<{ billingInterval: BillingInterval }> = ({ billingInterval }) => {
  const { refreshToken } = useContext(AuthContext);
  const { sm } = useContext(WindowContext);
  const { trialAvailable, openTrialModal, refresh: refreshAddon } = useContext(ResourceAddonContext);
  const { subscription, refresh: refreshSubscription } = useContext(BookitupSubscriptionContext);
  const activeAddons = subscription?.addons;
  const hasActiveLicence = subscription?.status === 'ACTIVE';
  const addons = DataS.billingAddons;

  const onAddonCheckoutSuccess = () => {
    refreshToken();
    refreshSubscription();
    refreshAddon();
  };

  return (
    <div>
      {sm ? (
        <Box sx={mobileCardSwiperSx}>
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            navigation
            pagination={{ clickable: true, dynamicBullets: addons.length > 4 }}
            slidesPerView={1.08}
            spaceBetween={16}
            centeredSlides
            grabCursor
          >
            {addons.map((addon) => (
              <SwiperSlide key={addon.name}>
                <AvailableAddonCard
                  addon={addon}
                  activeAddons={activeAddons}
                  billingInterval={billingInterval}
                  onAddonCheckoutSuccess={onAddonCheckoutSuccess}
                  openTrialModal={openTrialModal}
                  hasActiveLicence={hasActiveLicence}
                  trialAvailable={trialAvailable}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      ) : (
        <Flex gap="lg" wrap="wrap" justify="center" align="stretch">
          {addons.map((addon) => (
            <AvailableAddonCard
              key={addon.name}
              addon={addon}
              activeAddons={activeAddons}
              billingInterval={billingInterval}
              onAddonCheckoutSuccess={onAddonCheckoutSuccess}
              openTrialModal={openTrialModal}
              hasActiveLicence={hasActiveLicence}
              trialAvailable={trialAvailable}
            />
          ))}
        </Flex>
      )}
      <Space h="md" />
    </div>
  );
};

export default AvailableAddons;
